import { message, Modal } from 'ant-design-vue'
import { clearRegcodeFromStorage } from '@/utils/regcode'
import { extractAccessToken } from './authResponse'

/**
 * 密码 / 验证码登录：写入 token 与用户信息（与原 UserLogin 行为一致）
 * @returns {object|null} member
 */
export function applyCredentialAuth (store, responseData) {
  const token = extractAccessToken(responseData)
  if (token) {
    store.commit('user/SET_TOKEN', token)
  }
  if (responseData?.member || responseData?.userInfo) {
    const member = responseData.member || responseData.userInfo
    store.commit('user/SET_USER_INFO', member)
    store.dispatch('user/maybePromptBindMobile', { mobile: member?.mobile })
    return member
  }
  return null
}

/**
 * 微信授权登录：写入 token 与用户信息
 * @returns {{ token: string|null, member: object }}
 */
export function applyOAuthAuth (store, responseData) {
  const token = extractAccessToken(responseData)
  if (!token) return { token: null, member: {} }

  store.commit('user/SET_TOKEN', token)
  const member = responseData.member || responseData.userInfo || {}
  if (member && Object.keys(member).length) {
    store.commit('user/SET_USER_INFO', member)
  }
  store.dispatch('user/maybePromptBindMobile', { mobile: member?.mobile })
  return { token, member }
}

/**
 * 扫码登录：写入 token 与用户信息
 * @returns {{ token: string|null, member: object }}
 */
export function applyQrcodeAuth (store, responseData) {
  const token = extractAccessToken(responseData)
  if (!token) return { token: null, member: {} }

  store.commit('user/SET_TOKEN', token)
  const member = responseData.member || {}
  if (member && Object.keys(member).length) {
    store.commit('user/SET_USER_INFO', member)
  }
  store.dispatch('user/maybePromptBindMobile', { mobile: member?.mobile })
  return { token, member }
}

/** @param {object} member */
export function memberHasLoginPassword (member) {
  if (typeof member?.login_pwd === 'string') {
    return member.login_pwd.trim().length > 0
  }
  return !!member?.login_pwd
}

/**
 * 登录成功后统一收尾：刷新用户信息、清邀请码、客服群引导、跳转
 * @param {{ store: object, router: object, route: object, redirect?: string, res: object }} ctx
 */
export async function finishLoginAfterAuth ({ store, router, route, redirect, res }) {
  try {
    await store.dispatch('user/getUserInfo')
  } catch (err) {
    console.warn('获取用户信息失败', err)
  }

  message.success(res?.msg || res?.data?.msg || '登录成功')
  clearRegcodeFromStorage()

  try {
    const conf = await store.dispatch('common/getSystemConf')
    const groupUrl = (conf && conf.wechat_group) || store.getters['common/systemConf']?.wechat_group
    if (groupUrl && String(groupUrl).trim()) {
      localStorage.setItem('showWechatGroupDialog', '1')
    } else {
      localStorage.removeItem('showWechatGroupDialog')
    }
  } catch (e) {
    localStorage.removeItem('showWechatGroupDialog')
  }

  const { maybeRedirectMobileToH5 } = await import('@/utils/h5DeviceRedirect')
  if (maybeRedirectMobileToH5()) return

  const target = redirect || route.query?.redirect || '/'
  router.push(target)
}

/**
 * 验证码/扫码/授权登录后：未设密码则询问是否设置
 * @param {{ member: object, mobileFallback?: string, finishLoginFlow: Function, onPromptSetPassword: Function }} options
 */
export function promptFirstPasswordIfNeeded ({
  member,
  mobileFallback = '',
  finishLoginFlow,
  onPromptSetPassword
}) {
  const mobile = (member && member.mobile) || mobileFallback || ''
  if (!mobile) {
    return finishLoginFlow()
  }
  if (memberHasLoginPassword(member)) {
    return finishLoginFlow()
  }

  return new Promise((resolve) => {
    Modal.confirm({
      title: '提示',
      content: '检测到您还未设置登录密码，是否现在去设置？',
      okText: '是',
      cancelText: '否',
      onOk: async () => {
        onPromptSetPassword((member && member.mobile) || mobileFallback || '')
        resolve()
      },
      onCancel: async () => {
        await finishLoginFlow()
        resolve()
      }
    })
  })
}

/**
 * 扫码/授权/验证码登录成功后统一入口
 */
export async function completeSocialOrSmsLogin ({
  store,
  router,
  route,
  redirect,
  res,
  member,
  mobileFallback,
  onPromptSetPassword
}) {
  const finishLoginFlow = async () => {
    await finishLoginAfterAuth({ store, router, route, redirect, res })
  }
  await promptFirstPasswordIfNeeded({
    member,
    mobileFallback,
    finishLoginFlow,
    onPromptSetPassword
  })
}
